import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import Reveal, { Eyebrow } from "./Reveal";
import { toast } from "./ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USE_CASES = ["Collections", "Clinic", "Garage / service business", "Pharmacy", "Restaurant", "Other"];
const VOLUMES = ["Under 1,000 calls / month", "1,000 – 10,000 calls / month", "10,000 – 50,000 calls / month", "50,000+ calls / month"];

const inputCls =
  "w-full bg-transparent border-b border-zinc-300 focus:border-[#2563EB] outline-none py-3 text-base placeholder:text-zinc-400 transition-colors duration-300";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", use_case: "", volume: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.use_case) {
      toast.error("Please pick the use case closest to yours.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>Contact</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">
                Book a demo. Run a pilot.
              </h2>
              <p className="text-zinc-600 mt-6 leading-relaxed max-w-md">
                Tell us the use case and your rough monthly call volume. We'll come back with a demo
                on real flows for your business — then a pilot: one agent, one list, measured
                against your current results.
              </p>
              <p className="text-zinc-500 text-sm mt-6 leading-relaxed max-w-md">
                Contact us for pricing — no prices are published on this website.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.12}>
              {sent ? (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="contact-success-message"
                  className="bg-white border border-zinc-200 rounded-2xl p-10 text-center"
                >
                  <div className="w-12 h-12 mx-auto rounded-full bg-[#2563EB] flex items-center justify-center">
                    <ArrowRight size={20} className="text-white -rotate-45" />
                  </div>
                  <h3 className="font-display font-bold text-2xl mt-6">Enquiry received.</h3>
                  <p className="text-zinc-600 mt-3 max-w-sm mx-auto leading-relaxed">
                    Thanks, {form.name.split(" ")[0]}. We've got your note about {form.company} and
                    will reply to {form.email} shortly.
                  </p>
                </motion.div>
              ) : (
                <form data-testid="contact-form" onSubmit={submit} className="bg-white border border-zinc-200 rounded-2xl p-7 sm:p-10">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
                    <div>
                      <label htmlFor="cf-name" className="font-mono2 text-[10px] uppercase tracking-widest text-zinc-400">Full name</label>
                      <input id="cf-name" data-testid="contact-form-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Priya Sharma" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="cf-email" className="font-mono2 text-[10px] uppercase tracking-widest text-zinc-400">Work email</label>
                      <input id="cf-email" data-testid="contact-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="priya@company.com" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="cf-company" className="font-mono2 text-[10px] uppercase tracking-widest text-zinc-400">Company</label>
                      <input id="cf-company" data-testid="contact-form-company-input" required minLength={2} value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="cf-usecase" className="font-mono2 text-[10px] uppercase tracking-widest text-zinc-400">Use case</label>
                      <select id="cf-usecase" data-testid="contact-form-industry-select" required value={form.use_case} onChange={set("use_case")} className={`${inputCls} ${form.use_case ? "" : "text-zinc-400"} bg-white`}>
                        <option value="" disabled>Closest to yours…</option>
                        {USE_CASES.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="cf-volume" className="font-mono2 text-[10px] uppercase tracking-widest text-zinc-400">Monthly call volume</label>
                      <select id="cf-volume" data-testid="contact-form-volume-select" required value={form.volume} onChange={set("volume")} className={`${inputCls} ${form.volume ? "" : "text-zinc-400"} bg-white`}>
                        <option value="" disabled>Roughly…</option>
                        {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="cf-message" className="font-mono2 text-[10px] uppercase tracking-widest text-zinc-400">What should the agent do for you?</label>
                      <textarea id="cf-message" data-testid="contact-form-message-input" required minLength={10} rows={4} value={form.message} onChange={set("message")} placeholder="e.g. Remind 4,000 customers a month about upcoming EMIs and capture a payment date…" className={`${inputCls} resize-none`} />
                    </div>
                  </div>
                  <button
                    data-testid="contact-form-submit-button"
                    type="submit"
                    disabled={sending}
                    className="group mt-9 inline-flex items-center gap-2.5 bg-[#09090B] text-white font-medium pl-7 pr-6 py-4 rounded-full hover:bg-[#2563EB] disabled:opacity-60 transition-colors duration-300 w-full sm:w-auto justify-center"
                  >
                    {sending ? (
                      <>Sending <Loader2 size={17} className="animate-spin" /></>
                    ) : (
                      <>Send enquiry <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" /></>
                    )}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
